import React, { useEffect, useState } from "react";
import ReusableModal from "../../AppPAges/ManageSettings/ReusableModal";
import BiometricController from "./BiometricController";
import "./BiomatricPayroll.css";
import { ToastContainer } from "react-toastify";
import {
  CustomButton,
  RegularButton,
} from "../../../common/ReusableComponent/CustomButton";
import useFormattedDate from "../../../../context/useFormattingUtils";
import ReusableTable from "../../../common/ReusableComponent/reusableTable/ReusableTable";
import {
  ReusableDateInput,
  ReusableInput,
  SelectInput,
} from "../../../common/ReusableComponent/ReusableInput";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import PayrollReceiptPrint from "../../AppPAges/Payroll/SalaryRecipt";

const BiometricPayroll = () => {
  const {
    paySalaryModal,
    handleClose,
    handleOpen,
    selectedEmployee,
    payAmount,
    baseAmount,
    calculatedHours,
    payType,
    formatTime,
    dateFrom,
    dateTo,
    workingHoursPerDay,
    dailyLog,
    handlePaySalary,
    setPayType,
    setBaseAmount,
    setDateFrom,
    setDateTo,
    convertHoursToHMS,
    resetForm,
    handleWorkingHoursChange,
    loading,
    fetchSalaryDataForTable,
    handleEditSalary,
    editSalaryData,
    salaryHistoryRefreshKey,
    setSelectedEmployee,
    setSelectedEmployeeId,
    setSelectedBioEmployeeId,
    GetPaginatedData,
    openPrint,
    printData,
    handlePrint,
    setOpenPrint,
    setPrintData,
  } = BiometricController();

  const { formatDate } = useFormattedDate();

  /**
   * IMPORTANT FIX:
   * Earlier PaginatedSearchSelect had value={null}.
   * That caused the employee dropdown to reset on every re-render.
   * This local state keeps the selected employee object stable.
   */
  const [selectedPayrollEmployee, setSelectedPayrollEmployee] = useState(null);

  useEffect(() => {
    if (!paySalaryModal) {
      setSelectedPayrollEmployee(null);
      return;
    }

    /**
     * If editSalaryData already contains employee details, keep select populated.
     * Different APIs may return different keys, so this handles common shapes.
     */
    if (editSalaryData) {
      const employeeObject =
        editSalaryData.payroll_employee ||
        editSalaryData.employee ||
        editSalaryData.payroll_employee_data ||
        null;

      if (employeeObject && typeof employeeObject === "object") {
        setSelectedPayrollEmployee(employeeObject);
      }
    }
  }, [paySalaryModal, editSalaryData]);

  const columns = [
    { header: "Employee Name", field: "payroll_employee_name" },
    { header: "Total Hours", field: "total_hour" },
    { header: "From Date", field: "start_date" },
    { header: "To Date", field: "end_date" },
    { header: "Payroll Date", field: "payroll_date" },
    { header: "Unit", field: "salary_unit" },
    { header: "Amount", field: "total_amount" },
  ];

  const handleEmployeeSelect = (employee) => {
    if (employee) {
      setSelectedPayrollEmployee(employee);
      setSelectedEmployee(employee.id);
      setSelectedEmployeeId(employee.id);
      setSelectedBioEmployeeId(employee.employee_id);
    } else {
      setSelectedPayrollEmployee(null);
      setSelectedEmployee("");
      setSelectedEmployeeId("");
      setSelectedBioEmployeeId(null);
    }
  };

  const handleClearForm = () => {
    resetForm();
    setSelectedPayrollEmployee(null);
  };

  const handleModalClose = () => {
    handleClose();
    setSelectedPayrollEmployee(null);
  };

  return (
    <>
      <ToastContainer />

      <div className="responsive-box">
        <div className="employee-header">
          <div className="employee-title">Biometric Payroll</div>

          <div
            className="btn-position"
            style={{ display: "flex", gap: "10px" }}
          >
            <CustomButton
              text="Pay Salary"
              onClick={handleOpen}
              color="#000000"
              backgroundColor="#FFAF32"
            />
          </div>

          <ReusableModal
            title={editSalaryData ? "Edit Salary" : "Pay Salary"}
            show={paySalaryModal}
            handleClose={handleModalClose}
            width="670px"
            height="auto"
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handlePaySalary(e);
              }}
            >
              <div className="d-flex align-items-center mb-4 mx-3">
                <div style={{ width: "270px" }}>
                  <PaginatedSearchSelect
                    api="payrollEmployeeData"
                    GetPaginatedData={(api, params) => {
                      return GetPaginatedData(api, params).then((response) => ({
                        data: response.data || [],
                        totalCount: response.totalCount || 0,
                      }));
                    }}
                    getOptionLabel={(employee) =>
                      employee?.full_name || `Employee ${employee?.id || ""}`
                    }
                    getOptionValue={(employee) => employee?.id}
                    onSelect={handleEmployeeSelect}
                    value={selectedPayrollEmployee}
                    placeholder="Select Employee *"
                    disabled={!!editSalaryData}
                    style={{ width: "100%" }}
                  />
                </div>

                <ReusableInput
                  type="number"
                  name="working_hours_per_day"
                  label="Working Hours Per Day"
                  placeholder=""
                  value={workingHoursPerDay}
                  onChange={handleWorkingHoursChange}
                  required
                  style={{ width: "270px" }}
                />
              </div>

              <div className="d-flex align-items-center mb-4 mx-3">
                <ReusableDateInput
                  name="start_date"
                  label="Start Date"
                  required
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{ width: "270px" }}
                />

                <ReusableDateInput
                  name="end_date"
                  label="End Date"
                  required
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{ width: "270px" }}
                />
              </div>

              {calculatedHours && calculatedHours.total !== undefined && (
                <div className="d-flex align-items-center mb-4 mx-3">
                  <ReusableInput
                    type="number"
                    name="calculated_hour"
                    label="Calculated Hour"
                    placeholder=""
                    value={calculatedHours.total.toFixed(2)}
                    readOnly
                    required
                    style={{ width: "270px" }}
                  />

                  <SelectInput
                    value={payType || ""}
                    onChange={(e) => setPayType(e.target.value)}
                    required
                    options={[
                      { id: "Hourly", name: "Hourly" },
                      { id: "Weekly", name: "Weekly" },
                      { id: "Monthly", name: "Monthly" },
                    ]}
                    placeholder="Select pay type"
                    label="Pay Type"
                    style={{ width: "270px" }}
                  />
                </div>
              )}

              {calculatedHours && (
                <div className="hours-summary">
                  <h4>Calculated Hours</h4>

                  <div className="hours-grid">
                    <div>Total Hours: {calculatedHours.total.toFixed(2)}</div>
                    <div>
                      Week Worked: {calculatedHours.weeklyPercent.toFixed(1)}%
                    </div>
                    <div>
                      Month Worked: {calculatedHours.monthlyPercent.toFixed(1)}%
                    </div>
                  </div>

                  <div className="daily-log">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Day</th>
                          <th>In</th>
                          <th>Out</th>
                          <th>Hours</th>
                        </tr>
                      </thead>

                      <tbody>
                        {dailyLog.map((log, index) => (
                          <tr key={`${log.date}-${index}`}>
                            <td>{log.date}</td>
                            <td>{log.day}</td>
                            <td>{formatTime(log.clock_in_time)}</td>
                            <td>{formatTime(log.clock_out_time)}</td>
                            <td>{convertHoursToHMS(log.hours_worked)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="d-flex align-items-center mb-4 mx-3">
                <ReusableInput
                  type="number"
                  name="base_rate"
                  label="Base Rate"
                  placeholder=""
                  value={baseAmount}
                  onChange={(e) => setBaseAmount(e.target.value)}
                  required
                  style={{ width: "270px" }}
                />

                <ReusableInput
                  type="number"
                  name="pay_amount"
                  label="Total Payment Amount"
                  placeholder=""
                  value={payAmount}
                  readOnly
                  style={{ width: "270px" }}
                />
              </div>

              <div className="biomatric-payroll-form-button">
                {!editSalaryData && (
                  <RegularButton
                    text="Clear"
                    onClick={handleClearForm}
                    backgroundColor="#ffffff"
                    color="#282E26"
                  />
                )}

                <RegularButton
                  text="Confirm Payment"
                  onClick={handlePaySalary}
                  style={{ width: "200px" }}
                />
              </div>
            </form>
          </ReusableModal>
        </div>

        <ReusableTable
          fetchData={fetchSalaryDataForTable}
          columns={columns}
          showAction={true}
          enablePagination
          onPrint={handlePrint}
          onEdit={handleEditSalary}
          loading={loading}
          refreshKey={salaryHistoryRefreshKey}
        />

        {openPrint && printData && (
          <PayrollReceiptPrint
            open={openPrint}
            data={printData}
            onClose={() => {
              setOpenPrint(false);
              setPrintData(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default BiometricPayroll;
